import React from "react";

import { ArrowLeft, ArrowRight } from "lucide-react";

import useXFactorAccountBriefing from "../../../Hooks/useXFactorAccountBriefing";
import { PartialStatus } from "../../TempViewsRendering/components/Chrome";
import ExecutiveBrief from "../../TempViewsRendering/components/ExecutiveBrief";
import SituationCard from "../../TempViewsRendering/components/SituationCard";
import { formatDate } from "../../TempViewsRendering/lib/derive";

export type XFactorBriefingView = "situation" | "brief";

/**
 * The xfactor account briefing on the Account Synopsis page — Situation Card
 * and Executive Brief, with a control to move between them.
 *
 * Resolution is EB account → xfactor digest through the cache table's
 * `customer_id`. While a bulk CSV approve pipeline is still generating,
 * shows progress instead of a false empty-cache message.
 */
const XFactorAccountBriefing: React.FC<{
  accountId: string;
  accountName?: string | null;
  view: XFactorBriefingView;
  onViewChange: (view: XFactorBriefingView) => void;
  /** When true, skip the in-body date row and view toggle (parent Modal owns them). */
  hideChrome?: boolean;
  /** When true, section tabs are rendered by the parent Modal subheader. */
  hideSectionNav?: boolean;
}> = ({
  accountId,
  accountName,
  view,
  onViewChange,
  hideChrome = false,
  hideSectionNav = false,
}) => {
  const { latest, data, loading, error, pipeline } =
    useXFactorAccountBriefing(accountId);
  const name = latest?.account_name || accountName || "Unknown account";

  if (loading && !latest) {
    return <p className="m-0 text-sm text-gray-400">Loading briefing…</p>;
  }

  if (!latest && !data) {
    const briefingStatus = pipeline?.briefing_status;
    const accountStatus = pipeline?.account_status;
    const progress = pipeline?.progress;

    if (accountStatus === "failed") {
      return (
        <p className="m-0 text-sm text-amber-700">
          X-Factor account not created
          {pipeline?.account_error ? ` — ${pipeline.account_error}` : ""}.
          Briefing will not run until the account upsert succeeds.
        </p>
      );
    }

    if (briefingStatus === "failed") {
      return (
        <p className="m-0 text-sm text-amber-700">
          Briefing failed
          {pipeline?.briefing_error ? ` — ${pipeline.briefing_error}` : ""}.
        </p>
      );
    }

    if (
      briefingStatus === "queued" ||
      briefingStatus === "running" ||
      pipeline?.phase === "running"
    ) {
      const count =
        progress?.briefings_succeeded != null &&
        progress?.briefings_total != null
          ? ` (${progress.briefings_succeeded} / ${progress.briefings_total} in this batch)`
          : "";
      return (
        <p className="m-0 text-sm text-gray-500">Briefing generating…{count}</p>
      );
    }

    return (
      <p className="m-0 text-sm text-gray-400">
        No xfactor briefing cached for this account.
      </p>
    );
  }

  if (error && !data) {
    return <p className="m-0 text-sm text-amber-700">{error}</p>;
  }

  const isSituation = view === "situation";

  return (
    <div>
      {hideChrome ? null : (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-xs text-gray-500">
              {name}
              {latest?.digest_date
                ? ` · ${formatDate(latest.digest_date) || latest.digest_date}`
                : ""}
            </span>
            {latest?.is_partial ? (
              <PartialStatus missingSectionIds={latest.missing_section_ids} />
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => onViewChange(isSituation ? "brief" : "situation")}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#ad46ff" }}
          >
            {isSituation ? (
              <>
                Executive Brief <ArrowRight size={13} aria-hidden />
              </>
            ) : (
              <>
                <ArrowLeft size={13} aria-hidden /> Situation Card
              </>
            )}
          </button>
        </div>
      )}

      {hideChrome && latest?.is_partial ? (
        <div className="mb-3">
          <PartialStatus missingSectionIds={latest.missing_section_ids} />
        </div>
      ) : null}

      {isSituation ? (
        <SituationCard accountName={name} data={data} />
      ) : (
        <ExecutiveBrief
          accountName={name}
          data={data}
          hideSectionNav={hideSectionNav}
        />
      )}
    </div>
  );
};

export default XFactorAccountBriefing;
